import { FormikValues } from 'formik';
import React, { useCallback, useState } from 'react';
import { reportIncident } from '../../../service/RequestService.http';
import { AppRoutes, ROUTES } from '../../../common/constants/defaultStaticUrls';
import { CachedFormData } from './AssistanceFormContext.types';
import * as Sentry from '@sentry/react';

export type AssistanceFormContextData = {
  submitAssistanceForm: (values: FormikValues) => Promise<AppRoutes>;
  cachedFormData: CachedFormData;
  clearCachedFormData: () => void;
  reportId?: string;
};

type Props = {
  children?: React.ReactNode;
};

export const AssistanceFormContext = React.createContext<AssistanceFormContextData | null>(null);

export const AssistanceFormProvider: React.FC<Props> = ({ children }) => {
  const [cachedFormData, setCachedFormData] = useState<CachedFormData>({});
  const [reportId, setReportId] = useState<string>();

  const submitAssistanceForm = useCallback(async (values: FormikValues): Promise<AppRoutes> => {
    if (!values.latitude || !values.longitude) {
      return ROUTES.LOCALIZATION_ERROR;
    }

    const assistanceFormData = new FormData();
    assistanceFormData.append('file', values.file);
    assistanceFormData.append('phoneNumber', values.phoneNumber);
    assistanceFormData.append('latitude', values.latitude);
    assistanceFormData.append('longitude', values.longitude);

    const redirectPath = await reportIncident(assistanceFormData)
      .then((response) => {
        setReportId(response.referenceNumber);

        return ROUTES.SUCCESS;
      })
      .catch((err) => {
        Sentry.captureException(err);

        return ROUTES.PLATE_NUMBER_ERROR;
      });

    setCachedFormData({
      phoneNumber: values.phoneNumber,
    });

    return redirectPath;
  }, []);

  const clearCachedFormData = useCallback(() => {
    setCachedFormData({});
  }, []);

  const contextValue = React.useMemo(
    (): AssistanceFormContextData => ({
      submitAssistanceForm,
      cachedFormData,
      clearCachedFormData,
      reportId,
    }),
    [cachedFormData, submitAssistanceForm, clearCachedFormData, reportId],
  );

  return <AssistanceFormContext.Provider value={contextValue}>{children}</AssistanceFormContext.Provider>;
};
